import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Rocket, TrendingUp, TrendingDown, Activity } from "lucide-react";

const statusMeta = {
  winning: { color: "text-bm-green", border: "border-bm-green/40", icon: TrendingUp, label: "WINNING" },
  watching: { color: "text-bm-amber", border: "border-bm-amber/40", icon: Activity, label: "WATCHING" },
  losing: { color: "text-bm-danger", border: "border-bm-danger/40", icon: TrendingDown, label: "LOSING" },
};

function MetricRow({ label, value, suffix = "" }) {
  const v = parseInt(value, 10);
  const color = v >= 70 ? "text-bm-green" : v >= 40 ? "text-bm-amber" : "text-bm-danger";
  return (
    <div className="flex items-center justify-between text-xs font-mono py-1">
      <span className="text-bm-muted">{label}</span>
      <span className={color}>{value}{suffix}</span>
    </div>
  );
}

export default function LaunchIntel() {
  const [launches, setLaunches] = useState([]);

  useEffect(() => { api.launches().then(setLaunches); }, []);

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber">LAUNCH INTELLIGENCE</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          What we shipped.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-2xl">
          Post-launch SKU performance feeds back into the prediction system. Winners and losers both improve the model.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="launch-grid">
        {launches.map((sku, idx) => {
          const meta = statusMeta[sku.status];
          const Icon = meta.icon;
          return (
            <div key={sku.id} className={`bm-card p-4 border-l-2 ${meta.border} fade-up`} style={{ animationDelay: `${idx * 50}ms` }} data-testid={`launch-${sku.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="label-mono text-bm-dim">{sku.category}</span>
                <span className={`label-mono ${meta.color} flex items-center gap-1`}>
                  <Icon className="w-3 h-3" />{meta.label}
                </span>
              </div>
              <h3 className="font-heading text-lg font-bold leading-tight">{sku.name}</h3>
              <div className="label-mono text-bm-dim mt-1">SHIPPED {sku.launched_at}</div>

              <div className="mt-3 pt-3 border-t border-white/5 space-y-0.5">
                <MetricRow label="Repeat Rate" value={sku.repeat_rate} suffix="%" />
                <MetricRow label="Revenue Velocity" value={sku.revenue_velocity} />
                <MetricRow label="Review Sentiment" value={sku.review_sentiment} />
                <MetricRow label="Hook Performance" value={sku.hook_performance} />
                <MetricRow label="Creator Conversion" value={sku.creator_conversion} />
                <MetricRow label="UGC Created" value={sku.ugc_creation} />
              </div>

              <div className="mt-3 pt-3 border-t border-white/5">
                <div className="label-mono text-bm-dim mb-2">REGION SPLIT</div>
                {Object.entries(sku.region_performance).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-xs font-mono mb-1">
                    <span className="text-bm-muted w-16">{k}</span>
                    <div className="flex-1 h-1 bg-white/5">
                      <div className={`h-full ${v >= 70 ? "bg-bm-green" : v >= 40 ? "bg-bm-amber" : "bg-bm-danger"}`} style={{ width: `${v}%` }}></div>
                    </div>
                    <span className={v >= 70 ? "text-bm-green" : v >= 40 ? "text-bm-amber" : "text-bm-danger"}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
